import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Search, 
  Plus, 
  Server, 
  Activity, 
  Users,
  Zap
} from 'lucide-react'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { api } from '@/lib/api'

interface ServerInfo {
  server_id: string
  server_name: string
  port: number
  subdomain?: string
  server_url?: string
  transport: string
  status: string
  tools_count: number
  auth_supported: boolean
  cache_enabled?: boolean
  created_at?: string
}

interface BackendServerResponse {
  id: string
  name: string
  version: string
  port: number
  subdomain?: string
  transport: string
  status: string
  created_at: string
  updated_at: string
}

export function ServersPage() {
  const [servers, setServers] = useState<ServerInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchServers()
  }, [])

  const fetchServers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/servers')
      if (response && typeof response === 'object' && 'servers' in response) {
        const backendServers: BackendServerResponse[] = (response as any).servers || []
        
        // Map backend response to frontend interface
        const mappedServers: ServerInfo[] = backendServers.map(server => {
          // Generate server URL based on subdomain or port
          let serverUrl = ''
          if (server.subdomain) {
            // Subdomain-based URL
            const protocol = server.transport === 'websocket' ? 'wss://' : 'https://'
            const currentDomain = window.location.hostname
            const baseDomain = currentDomain === 'localhost' ? 'localhost' : currentDomain
            serverUrl = `${protocol}${server.subdomain}.${baseDomain}/mcp`
          } else {
            // Port-based URL (legacy)
            const protocol = server.transport === 'websocket' ? 'ws://' : 'http://'
            serverUrl = `${protocol}${window.location.hostname}:${server.port}/mcp`
          }
          
          return {
            server_id: server.id,
            server_name: server.name,
            port: server.port,
            subdomain: server.subdomain,
            server_url: serverUrl,
            transport: server.transport,
            status: server.status,
            tools_count: 0, // Default value - this would need to be added to backend response
            auth_supported: false, // Default value - this would need to be added to backend response
            cache_enabled: false, // Default value - this would need to be added to backend response
            created_at: server.created_at
          }
        })
        
        setServers(mappedServers)
      }
    } catch (error) {
      console.error('Failed to fetch servers:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredServers = servers.filter(server =>
    (server.server_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (server.server_id || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running': return 'bg-green-500'
      case 'stopped': return 'bg-red-500'
      case 'starting': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  const getTransportIcon = (transport: string) => {
    switch (transport.toLowerCase()) {
      case 'websocket': return <Zap className="h-4 w-4" />
      case 'http': return <Server className="h-4 w-4" />
      case 'sse': return <Activity className="h-4 w-4" />
      default: return <Server className="h-4 w-4" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">MCP Servers</h1>
          <p className="text-muted-foreground">
            Manage your OpenAPI-generated MCP servers
          </p>
        </div>
        <Button asChild>
          <Link to="/servers/new" className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Create Server
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search servers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Servers Grid */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredServers.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold">No servers found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery 
                ? "No servers match your search criteria" 
                : "Get started by creating your first MCP server"}
            </p>
            {!searchQuery && (
              <Button asChild>
                <Link to="/servers/new" className="flex items-center">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Server
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredServers.map((server) => (
            <Card key={server.server_id || 'unknown'} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg truncate">
                    {server.server_name || 'Unknown Server'}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <div 
                      className={`w-2 h-2 rounded-full ${getStatusColor(server.status || 'unknown')}`}
                      title={server.status || 'unknown'}
                    />
                    <Badge variant="outline" className="text-xs">
                      {server.status || 'unknown'}
                    </Badge>
                  </div>
                </div>
                <CardDescription className="font-mono text-xs">
                  {server.server_id || 'unknown-id'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Transport</span>
                    <div className="flex items-center space-x-1">
                      {getTransportIcon(server.transport || 'unknown')}
                      <span className="capitalize">{server.transport || 'unknown'}</span>
                    </div>
                  </div>
                  
                  {server.subdomain ? (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Subdomain</span>
                      <span className="font-mono text-xs">{server.subdomain}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Port</span>
                      <span className="font-mono">{server.port || 'N/A'}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">URL</span>
                    <div className="flex items-center space-x-1">
                      <span className="font-mono text-xs truncate max-w-[120px]" title={server.server_url}>
                        {server.server_url}
                      </span>
                      <button 
                        onClick={() => navigator.clipboard.writeText(server.server_url || '')}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Copy URL"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tools</span>
                    <span>{server.tools_count || 0}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-xs">
                    {server.auth_supported && (
                      <Badge variant="secondary">
                        <Users className="mr-1 h-3 w-3" />
                        Auth
                      </Badge>
                    )}
                    {server.cache_enabled && (
                      <Badge variant="secondary">
                        <Zap className="mr-1 h-3 w-3" />
                        Cache
                      </Badge>
                    )}
                  </div>

                  <div className="pt-2">
                    <Button asChild variant="outline" className="w-full">
                      <Link to={`/servers/${server.server_id || 'unknown'}`}>
                        View Details
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
} 